import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import colors from '../../../styles/colors';
import { useAppSelector } from '../../../store/hooks';
import { apiGetMyCardAccount } from '../../../api/userCardApi';
import DailyClaimButton from '../../../components/Cards/DailyClaimButton';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px 70px;
  min-height: calc(100vh - 70px);
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: gold;
  margin-bottom: 6px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 32px;
  text-align: center;
`;

const CreditsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 8px;
  padding: 10px 18px;
  margin-bottom: 28px;
  width: 100%;
  max-width: 340px;
  justify-content: center;
`;

const CreditsLabel = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CreditsValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: gold;
`;

const ButtonsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 340px;
`;

const NavBtn = styled.button<{ $accent: string; $cta?: boolean }>`
  width: 100%;
  padding: 16px 18px;
  background-color: ${({ $cta }) => $cta ? 'rgba(34, 197, 94, 0.1)' : colors.bgColorBlack2};
  border: 2px solid ${({ $accent }) => $accent};
  border-radius: 10px;
  color: ${({ $accent }) => $accent};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.15s;

  &:active {
    background-color: ${({ $accent }) => $accent}1a;
  }
`;

const BtnLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const BtnLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
`;

const BtnSub = styled.span`
  font-size: 11px;
  font-weight: 400;
  opacity: 0.6;
`;

const BtnArrow = styled.span`
  font-size: 18px;
  opacity: 0.5;
`;

const CardsLandingMobile: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );
  const [credits, setCredits] = useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiGetMyCardAccount()
      .then((data) => setCredits(data.credits))
      .catch(() => setCredits(null));
  }, [isAuthenticated]);

  return (
    <Container>
      <Title>GWF Cards</Title>
      <Subtitle>La collezione ufficiale di carte GWF</Subtitle>

      {isAuthenticated && (
        <>
          <CreditsBar>
            <CreditsLabel>Crediti:</CreditsLabel>
            <CreditsValue>{credits !== null ? credits : '—'}</CreditsValue>
          </CreditsBar>
          <DailyClaimButton onClaimed={(c) => setCredits(c)} />
        </>
      )}

      <ButtonsCol>
        <NavBtn $accent="#4a9eff" onClick={() => navigate('/carte/lista')}>
          <BtnLeft>
            <BtnLabel>Lista Carte</BtnLabel>
            <BtnSub>Sfoglia il catalogo completo</BtnSub>
          </BtnLeft>
          <BtnArrow>›</BtnArrow>
        </NavBtn>

        {isAuthenticated && (
          <NavBtn $accent="#22c55e" $cta onClick={() => navigate('/carte/pacchetti')}>
            <BtnLeft>
              <BtnLabel>Acquista Pacchetti</BtnLabel>
              <BtnSub>Apri nuovi pacchetti carte</BtnSub>
            </BtnLeft>
            <BtnArrow>›</BtnArrow>
          </NavBtn>
        )}

        {isAuthenticated && (
          <NavBtn $accent="#f97316" onClick={() => navigate('/carte/negozio')}>
            <BtnLeft>
              <BtnLabel>Negozio Carte</BtnLabel>
              <BtnSub>Acquista singole carte</BtnSub>
            </BtnLeft>
            <BtnArrow>›</BtnArrow>
          </NavBtn>
        )}

        <NavBtn $accent="gold" onClick={() => navigate('/carte/classifica')}>
          <BtnLeft>
            <BtnLabel>Classifica</BtnLabel>
            <BtnSub>I migliori collezionisti GWF</BtnSub>
          </BtnLeft>
          <BtnArrow>›</BtnArrow>
        </NavBtn>

        <NavBtn $accent="#a855f7" onClick={() => navigate('/carte/espansioni')}>
          <BtnLeft>
            <BtnLabel>Espansioni</BtnLabel>
            <BtnSub>La tua progressione per set</BtnSub>
          </BtnLeft>
          <BtnArrow>›</BtnArrow>
        </NavBtn>

        <NavBtn $accent="#38bdf8" onClick={() => navigate('/carte/quiz')}>
          <BtnLeft>
            <BtnLabel>Quiz</BtnLabel>
            <BtnSub>Metti alla prova le tue conoscenze</BtnSub>
          </BtnLeft>
          <BtnArrow>›</BtnArrow>
        </NavBtn>

        <NavBtn $accent="#38bdf8" onClick={() => navigate('/carte/quiz/classifica')}>
          <BtnLeft>
            <BtnLabel>Classifica Quiz</BtnLabel>
            <BtnSub>Chi risponde meglio alle domande</BtnSub>
          </BtnLeft>
          <BtnArrow>›</BtnArrow>
        </NavBtn>

        {isAuthenticated && (
          <NavBtn $accent="#10b981" onClick={() => navigate('/carte/cedi')}>
            <BtnLeft>
              <BtnLabel>Cedi Carte</BtnLabel>
              <BtnSub>Cedi le copie in eccesso per crediti</BtnSub>
            </BtnLeft>
            <BtnArrow>›</BtnArrow>
          </NavBtn>
        )}

        <NavBtn $accent="#f43f5e" onClick={() => navigate('/carte/introvabili')}>
          <BtnLeft>
            <BtnLabel>Introvabili</BtnLabel>
            <BtnSub>Le carte meno possedute dalla community</BtnSub>
          </BtnLeft>
          <BtnArrow>›</BtnArrow>
        </NavBtn>

        {isAuthenticated && (
          <NavBtn $accent="rgba(180,180,180,0.9)" onClick={() => navigate('/carte/storico')}>
            <BtnLeft>
              <BtnLabel>Storico</BtnLabel>
              <BtnSub>Crediti guadagnati e spesi</BtnSub>
            </BtnLeft>
            <BtnArrow>›</BtnArrow>
          </NavBtn>
        )}
      </ButtonsCol>
    </Container>
  );
};

export default CardsLandingMobile;
